import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables')
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true
  }
})

// Helper functions for common operations

// ============================================
// AUTH HELPERS
// ============================================

export const signUp = async (email, password, fullName) => {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    options: {
      data: {
        full_name: fullName
      }
    }
  })
  return { data, error }
}

export const signIn = async (email, password) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  })
  return { data, error }
}

export const signOut = async () => {
  const { error } = await supabase.auth.signOut()
  return { error }
}

export const resetPassword = async (email) => {
  const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}/reset-password`
  })
  return { data, error }
}

export const updatePassword = async (newPassword) => {
  const { data, error } = await supabase.auth.updateUser({
    password: newPassword
  })
  return { data, error }
}

// ============================================
// PROFILE HELPERS
// ============================================

export const getProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('id', userId)
    .single()
  return { data, error }
}

export const updateProfile = async (userId, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', userId)
    .select()
    .single()
  return { data, error }
}

export const getAllProfiles = async () => {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .order('created_at', { ascending: false })
  return { data, error }
}

// ============================================
// INVITATION HELPERS
// ============================================

export const createInvitation = async (email, fullName, invitedBy) => {
  const { data, error } = await supabase
    .from('invitations')
    .insert({
      email,
      full_name: fullName,
      invited_by: invitedBy
    })
    .select()
    .single()
  return { data, error }
}

export const getInvitationByToken = async (token) => {
  const { data, error } = await supabase
    .from('invitations')
    .select('*')
    .eq('token', token)
    .eq('status', 'pending')
    .gt('expires_at', new Date().toISOString())
    .maybeSingle()
  return { data, error }
}

export const getAllInvitations = async () => {
  const { data, error } = await supabase
    .from('invitations')
    .select('*, invited_by_profile:profiles!invited_by(full_name)')
    .order('created_at', { ascending: false })
  return { data, error }
}

// ============================================
// VEHICLE HELPERS
// ============================================

export const getVehicles = async (ownerId = null) => {
  let query = supabase
    .from('vehicles')
    .select(`
      *,
      owner:profiles!owner_id(id, full_name, email)
    `)
    .eq('is_active', true)
    .order('created_at', { ascending: false })

  if (ownerId) {
    query = query.eq('owner_id', ownerId)
  }

  const { data, error } = await query
  return { data, error }
}

export const getVehicle = async (vehicleId) => {
  // Try with images join first, fall back without if vehicle_images table doesn't exist
  let { data, error } = await supabase
    .from('vehicles')
    .select(`
      *,
      owner:profiles!owner_id(id, full_name, email),
      images:vehicle_images(*)
    `)
    .eq('id', vehicleId)
    .single()

  // If the join failed (table doesn't exist), retry without images
  if (error && !data) {
    const retry = await supabase
      .from('vehicles')
      .select(`
        *,
        owner:profiles!owner_id(id, full_name, email)
      `)
      .eq('id', vehicleId)
      .single()
    data = retry.data
    error = retry.error
  }

  return { data, error }
}

export const createVehicle = async (vehicleData) => {
  const { data, error } = await supabase
    .from('vehicles')
    .insert(vehicleData)
    .select()
    .single()
  return { data, error }
}

export const updateVehicle = async (vehicleId, updates) => {
  const { data, error } = await supabase
    .from('vehicles')
    .update(updates)
    .eq('id', vehicleId)
    .select()
    .single()
  return { data, error }
}

export const deleteVehicle = async (vehicleId) => {
  const { data, error } = await supabase
    .from('vehicles')
    .update({ is_active: false })
    .eq('id', vehicleId)
  return { data, error }
}

// ============================================
// SERVICE RECORD HELPERS
// ============================================

export const getServiceRecords = async (vehicleId = null) => {
  let query = supabase
    .from('service_records')
    .select(`
      *,
      vehicle:vehicles(id, year, make, model, owner_id)
    `)
    .order('service_date', { ascending: false })

  if (vehicleId) {
    query = query.eq('vehicle_id', vehicleId)
  }

  const { data, error } = await query
  return { data, error }
}

export const createServiceRecord = async (recordData) => {
  const { data, error } = await supabase
    .from('service_records')
    .insert(recordData)
    .select()
    .single()
  return { data, error }
}

export const updateServiceRecord = async (recordId, updates) => {
  const { data, error } = await supabase
    .from('service_records')
    .update(updates)
    .eq('id', recordId)
    .select()
    .single()
  return { data, error }
}

export const deleteServiceRecord = async (recordId) => {
  const { error } = await supabase
    .from('service_records')
    .delete()
    .eq('id', recordId)
  return { error }
}

// ============================================
// APPOINTMENT HELPERS
// ============================================

export const getAppointments = async (ownerId = null) => {
  let query = supabase
    .from('appointments')
    .select(`
      *,
      vehicle:vehicles(id, year, make, model),
      owner:profiles!owner_id(id, full_name)
    `)
    .order('scheduled_date', { ascending: true })

  if (ownerId) {
    query = query.eq('owner_id', ownerId)
  }

  const { data, error } = await query
  return { data, error }
}

export const createAppointment = async (appointmentData) => {
  const { data, error } = await supabase
    .from('appointments')
    .insert(appointmentData)
    .select()
    .single()
  return { data, error }
}

export const updateAppointment = async (appointmentId, updates) => {
  const { data, error } = await supabase
    .from('appointments')
    .update(updates)
    .eq('id', appointmentId)
    .select()
    .single()
  return { data, error }
}

export const deleteAppointment = async (appointmentId) => {
  const { error } = await supabase
    .from('appointments')
    .delete()
    .eq('id', appointmentId)
  return { error }
}

// ============================================
// DOCUMENT HELPERS
// ============================================

export const getDocuments = async (ownerId = null, vehicleId = null) => {
  let query = supabase
    .from('documents')
    .select(`
      *,
      vehicle:vehicles(id, year, make, model)
    `)
    .order('created_at', { ascending: false })

  if (ownerId) {
    query = query.eq('owner_id', ownerId)
  }
  if (vehicleId) {
    query = query.eq('vehicle_id', vehicleId)
  }

  const { data, error } = await query

  // Resolve signed URLs for documents stored in private bucket
  if (data && data.length > 0) {
    const docsNeedingUrls = data.filter(d =>
      d.document_url && !d.document_url.startsWith('http') && !d.document_url.startsWith('//')
    )
    if (docsNeedingUrls.length > 0) {
      const paths = docsNeedingUrls.map(d => d.document_url)
      const { data: signedUrls } = await supabase.storage
        .from('documents')
        .createSignedUrls(paths, 3600) // 1 hour expiry
      if (signedUrls) {
        const urlMap = {}
        signedUrls.forEach((su) => {
          if (su.signedUrl) urlMap[su.path] = su.signedUrl
        })
        data.forEach(d => {
          if (urlMap[d.document_url]) {
            d.document_url = urlMap[d.document_url]
          }
        })
      }
    }
  }

  return { data, error }
}

export const createDocument = async (documentData) => {
  const { data, error } = await supabase
    .from('documents')
    .insert(documentData)
    .select()
    .single()
  return { data, error }
}

export const deleteDocument = async (documentId) => {
  const { error } = await supabase
    .from('documents')
    .delete()
    .eq('id', documentId)
  return { error }
}

// ============================================
// CONVERSATION & MESSAGE HELPERS
// ============================================

export const getConversations = async (clientId = null) => {
  let query = supabase
    .from('conversations')
    .select(`
      *,
      client:profiles!client_id(id, full_name, email),
      vehicle:vehicles(id, year, make, model),
      messages(id, content, sender_type, is_read, created_at)
    `)
    .eq('is_archived', false)
    .order('last_message_at', { ascending: false })

  if (clientId) {
    query = query.eq('client_id', clientId)
  }

  const { data, error } = await query
  return { data, error }
}

export const getConversation = async (conversationId) => {
  const { data, error } = await supabase
    .from('conversations')
    .select(`
      *,
      client:profiles!client_id(id, full_name, email),
      vehicle:vehicles(id, year, make, model)
    `)
    .eq('id', conversationId)
    .single()
  return { data, error }
}

export const createConversation = async (clientId, subject = null, vehicleId = null) => {
  const { data, error } = await supabase
    .from('conversations')
    .insert({
      client_id: clientId,
      subject,
      vehicle_id: vehicleId
    })
    .select()
    .single()
  return { data, error }
}

export const getMessages = async (conversationId) => {
  const { data, error } = await supabase
    .from('messages')
    .select(`
      *,
      sender:profiles!sender_id(id, full_name)
    `)
    .eq('conversation_id', conversationId)
    .order('created_at', { ascending: true })
  return { data, error }
}

export const sendMessage = async (conversationId, senderId, senderType, content) => {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      conversation_id: conversationId,
      sender_id: senderId,
      sender_type: senderType,
      content
    })
    .select()
    .single()
  return { data, error }
}

export const markMessagesAsRead = async (conversationId, senderType) => {
  const { error } = await supabase
    .from('messages')
    .update({ is_read: true, read_at: new Date().toISOString() })
    .eq('conversation_id', conversationId)
    .eq('sender_type', senderType)
    .eq('is_read', false)
  return { error }
}

// ============================================
// STORAGE HELPERS
// ============================================

export const uploadFile = async (bucket, path, file) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false
    })
  return { data, error }
}

export const getFileUrl = (bucket, path) => {
  const { data } = supabase.storage.from(bucket).getPublicUrl(path)
  return data.publicUrl
}

export const getSignedUrl = async (bucket, path, expiresIn = 3600) => {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresIn)
  return { data, error }
}

export const deleteFile = async (bucket, path) => {
  const { error } = await supabase.storage.from(bucket).remove([path])
  return { error }
}

// ============================================
// REALTIME SUBSCRIPTIONS
// ============================================

export const subscribeToMessages = (conversationId, callback) => {
  return supabase
    .channel(`messages:${conversationId}`)
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'messages',
        filter: `conversation_id=eq.${conversationId}`
      },
      callback
    )
    .subscribe()
}

export const subscribeToConversations = (clientId, callback) => {
  return supabase
    .channel(`conversations:${clientId}`)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'conversations',
        filter: `client_id=eq.${clientId}`
      },
      callback
    )
    .subscribe()
}

// Unsubscribe helper
export const unsubscribe = (channel) => {
  supabase.removeChannel(channel)
}

// ============================================
// CONCIERGE WAITLIST HELPERS
// ============================================

export const addToWaitlist = async (data) => {
  const { data: result, error } = await supabase
    .from('concierge_waitlist')
    .insert({
      email: data.email,
      phone: data.phone || null,
      full_name: data.fullName || null,
      interest: data.interest || 'general',
      user_id: data.userId || null,
      source: data.source || 'website'
    })
    .select()
    .single()
  return { data: result, error }
}

export const checkWaitlistStatus = async (email, userId = null) => {
  let query = supabase
    .from('concierge_waitlist')
    .select('*')
    .eq('email', email)

  if (userId) {
    query = query.or(`user_id.eq.${userId}`)
  }

  const { data, error } = await query.maybeSingle()
  return { data, error }
}

export const getWaitlistEntries = async (options = {}) => {
  let query = supabase
    .from('concierge_waitlist')
    .select('*')
    .order('created_at', { ascending: false })

  if (options.status) {
    query = query.eq('status', options.status)
  }

  if (options.limit) {
    query = query.limit(options.limit)
  }

  const { data, error } = await query
  return { data, error }
}

export const updateWaitlistEntry = async (id, updates) => {
  const { data, error } = await supabase
    .from('concierge_waitlist')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

export const deleteWaitlistEntry = async (id) => {
  const { error } = await supabase
    .from('concierge_waitlist')
    .delete()
    .eq('id', id)
  return { error }
}

export const getWaitlistStats = async () => {
  const { data, error } = await supabase
    .from('concierge_waitlist')
    .select('status, interest')

  if (error) return { data: null, error }

  const stats = {
    total: data.length,
    pending: data.filter(e => e.status === 'pending').length,
    contacted: data.filter(e => e.status === 'contacted').length,
    enrolled: data.filter(e => e.status === 'enrolled').length,
    byInterest: {}
  }

  data.forEach(entry => {
    const interest = entry.interest || 'general'
    stats.byInterest[interest] = (stats.byInterest[interest] || 0) + 1
  })

  return { data: stats, error: null }
}

// ============================================
// VEHICLE VALUATION HELPERS
// ============================================

export const getVehicleValuations = async (vehicleId) => {
  const { data, error } = await supabase
    .from('vehicle_valuations')
    .select('*')
    .eq('vehicle_id', vehicleId)
    .order('valuation_date', { ascending: false })
  return { data, error }
}

export const addValuation = async (valuationData) => {
  const { data, error } = await supabase
    .from('vehicle_valuations')
    .insert(valuationData)
    .select()
    .single()
  return { data, error }
}

// ============================================
// MARKET VALUATIONS HELPERS (AI-powered)
// ============================================

// Get the latest market valuation for a vehicle
export const getMarketValuation = async (vehicleId) => {
  const { data, error } = await supabase
    .from('market_valuations')
    .select('*')
    .eq('vehicle_id', vehicleId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle()
  return { data, error }
}

// Get all market valuations for a vehicle (history)
export const getMarketValuationHistory = async (vehicleId) => {
  const { data, error } = await supabase
    .from('market_valuations')
    .select('*')
    .eq('vehicle_id', vehicleId)
    .order('created_at', { ascending: false })
  return { data, error }
}

// Get latest market valuations for all vehicles owned by a user
export const getAllMarketValuations = async (ownerId) => {
  // First get all vehicle IDs for this owner
  const { data: vehicles, error: vError } = await supabase
    .from('vehicles')
    .select('id')
    .eq('owner_id', ownerId)
    .eq('is_active', true)

  if (vError || !vehicles || vehicles.length === 0) {
    return { data: [], error: vError }
  }

  const vehicleIds = vehicles.map(v => v.id)

  // Get latest valuation for each vehicle using a subquery approach
  const { data, error } = await supabase
    .from('market_valuations')
    .select('*')
    .in('vehicle_id', vehicleIds)
    .order('created_at', { ascending: false })

  if (error) return { data: null, error }

  // Deduplicate to get only the latest per vehicle
  const latestByVehicle = {}
  data.forEach(v => {
    if (!latestByVehicle[v.vehicle_id]) {
      latestByVehicle[v.vehicle_id] = v
    }
  })

  return { data: Object.values(latestByVehicle), error: null }
}

// Request a fresh valuation from the API
export const requestMarketValuation = async (vehicle, force = false) => {
  // Longer timeout for web-search-powered valuations (up to 55 seconds)
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), 55000)

  const response = await fetch('/api/valuation/estimate', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    signal: controller.signal,
    body: JSON.stringify({
      vehicleId: vehicle.id,
      year: vehicle.year,
      make: vehicle.make,
      model: vehicle.model,
      trim: vehicle.trim,
      color: vehicle.color,
      mileage: vehicle.mileage || vehicle.current_mileage,
      condition: vehicle.condition,
      notes: vehicle.notes,
      force
    })
  })
  clearTimeout(timeoutId)

  const data = await response.json()
  if (!response.ok || !data.success) {
    const error = new Error(data.error || 'Failed to get valuation')
    error.status = response.status
    error.errorCode = data.error
    throw error
  }
  return { data: data.valuation, cached: data.cached, count: data.count, error: null }
}

// ============================================
// PROVIDER REVIEWS HELPERS
// ============================================

export const getProviderReviews = async (providerId) => {
  const { data, error } = await supabase
    .from('provider_reviews')
    .select('*, profiles:reviewer_id(full_name, avatar_url)')
    .eq('provider_id', providerId)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
  return { data: data || [], error }
}

export const getProviderReviewSummary = async (providerId) => {
  const { data, error } = await supabase
    .from('provider_reviews')
    .select('rating')
    .eq('provider_id', providerId)
    .eq('is_active', true)

  if (error || !data || data.length === 0) {
    return { data: { averageRating: null, reviewCount: 0 }, error }
  }

  const avg = data.reduce((sum, r) => sum + r.rating, 0) / data.length
  return {
    data: {
      averageRating: Math.round(avg * 10) / 10,
      reviewCount: data.length
    },
    error: null
  }
}

export const submitProviderReview = async (reviewData) => {
  // Check if user already reviewed this provider
  const { data: existing } = await supabase
    .from('provider_reviews')
    .select('id')
    .eq('provider_id', reviewData.provider_id)
    .eq('reviewer_id', reviewData.reviewer_id)
    .eq('is_active', true)
    .single()

  if (existing) {
    // Update existing review
    const { data, error } = await supabase
      .from('provider_reviews')
      .update({
        rating: reviewData.rating,
        review_text: reviewData.review_text,
        service_type: reviewData.service_type,
        vehicle_info: reviewData.vehicle_info,
        updated_at: new Date().toISOString()
      })
      .eq('id', existing.id)
      .select()
      .single()
    return { data, error, updated: true }
  }

  // Create new review
  const { data, error } = await supabase
    .from('provider_reviews')
    .insert({
      provider_id: reviewData.provider_id,
      reviewer_id: reviewData.reviewer_id,
      rating: reviewData.rating,
      review_text: reviewData.review_text,
      service_type: reviewData.service_type,
      vehicle_info: reviewData.vehicle_info
    })
    .select()
    .single()
  return { data, error, updated: false }
}

export const deleteProviderReview = async (reviewId) => {
  const { error } = await supabase
    .from('provider_reviews')
    .update({ is_active: false })
    .eq('id', reviewId)
  return { error }
}

// ============================================
// SETTINGS & PREFERENCES HELPERS
// ============================================

export const updatePublicProfileSetting = async (userId, isPublic) => {
  const { data, error } = await supabase
    .from('profiles')
    .update({ public_profile: isPublic })
    .eq('id', userId)
    .select()
    .single()
  return { data, error }
}

export const getReminderPreferences = async (userId) => {
  const { data, error } = await supabase
    .from('reminder_preferences')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle()

  // Return defaults if no preferences found
  if (!data && !error) {
    return {
      data: {
        email_reminders: true,
        sms_reminders: false,
        maintenance_alerts: true,
        appointment_reminders: true,
        newsletter: true
      },
      error: null
    }
  }
  return { data, error }
}

export const updateReminderPreferences = async (userId, preferences) => {
  // Try to update first
  const { data: existing } = await supabase
    .from('reminder_preferences')
    .select('id')
    .eq('user_id', userId)
    .maybeSingle()

  if (existing) {
    const { data, error } = await supabase
      .from('reminder_preferences')
      .update(preferences)
      .eq('user_id', userId)
      .select()
      .single()
    return { data, error }
  } else {
    const { data, error } = await supabase
      .from('reminder_preferences')
      .insert({ user_id: userId, ...preferences })
      .select()
      .single()
    return { data, error }
  }
}

export const getCollectionValue = async (ownerId) => {
  const { data, error } = await supabase
    .from('vehicles')
    .select('estimated_value')
    .eq('owner_id', ownerId)
    .eq('is_active', true)

  if (error) return { data: null, error }

  const totalValue = data.reduce((sum, v) => sum + (v.estimated_value || 0), 0)
  return { data: { total: totalValue, count: data.length }, error: null }
}

// ============================================
// VEHICLE VISIBILITY HELPERS
// ============================================

export const updateVehicleVisibility = async (vehicleId, isPublic) => {
  const { data, error } = await supabase
    .from('vehicles')
    .update({ is_public: isPublic })
    .eq('id', vehicleId)
    .select()
    .single()
  return { data, error }
}

// ============================================
// PUBLIC GARAGE HELPERS
// ============================================

export const getPublicProfile = async (userId) => {
  const { data, error } = await supabase
    .from('profiles')
    .select('id, full_name, avatar_url, member_since, company_name')
    .eq('id', userId)
    .eq('public_profile', true)
    .maybeSingle()
  return { data, error }
}

export const getPublicGarageVehicles = async (userId) => {
  const { data, error } = await supabase
    .from('vehicles')
    .select('id, year, make, model, color, status, mileage, primary_image_url, created_at')
    .eq('owner_id', userId)
    .eq('is_public', true)
    .eq('is_active', true)
    .order('created_at', { ascending: false })
  return { data, error }
}

export const getPublicVehicleDetails = async (vehicleId, userId) => {
  const { data, error } = await supabase
    .from('vehicles')
    .select('id, year, make, model, trim, color, status, mileage, primary_image_url, notes, created_at')
    .eq('id', vehicleId)
    .eq('owner_id', userId)
    .eq('is_public', true)
    .eq('is_active', true)
    .maybeSingle()
  return { data, error }
}

// ============================================
// ADMIN PROFILE MANAGEMENT HELPERS
// ============================================

export const updateClientProfile = async (clientId, updates) => {
  const { data, error } = await supabase
    .from('profiles')
    .update(updates)
    .eq('id', clientId)
    .select()
    .single()
  return { data, error }
}

// ============================================
// SERVICE PROVIDER HELPERS
// ============================================

export const getServiceProviders = async (filters = {}) => {
  let query = supabase
    .from('service_providers')
    .select('*')
    .eq('is_active', true)
    .order('featured', { ascending: false })
    .order('name', { ascending: true })

  if (filters.category && filters.category !== 'all') {
    query = query.eq('category', filters.category)
  }
  if (filters.region) {
    query = query.eq('region', filters.region)
  }
  if (filters.claim_status) {
    query = query.eq('claim_status', filters.claim_status)
  }

  const { data, error } = await query
  return { data, error }
}

export const getServiceProvider = async (id) => {
  const { data, error } = await supabase
    .from('service_providers')
    .select('*')
    .eq('id', id)
    .maybeSingle()
  return { data, error }
}

export const getServiceProviderBySlug = async (slug) => {
  const { data, error } = await supabase
    .from('service_providers')
    .select('*')
    .eq('slug', slug)
    .maybeSingle()
  return { data, error }
}

export const updateServiceProvider = async (id, updates) => {
  const { data, error } = await supabase
    .from('service_providers')
    .update({ ...updates, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select()
    .single()
  return { data, error }
}

export const getMyProviderListing = async (userId) => {
  const { data, error } = await supabase
    .from('service_providers')
    .select('*')
    .eq('claimed_by', userId)
    .maybeSingle()
  return { data, error }
}

export const submitClaimRequest = async (claimData) => {
  const { data, error } = await supabase
    .from('provider_claim_requests')
    .insert(claimData)
    .select()
    .single()
  return { data, error }
}

export const getClaimRequests = async (status = null) => {
  let query = supabase
    .from('provider_claim_requests')
    .select('*, provider:service_providers(id, name)')
    .order('created_at', { ascending: false })

  if (status) {
    query = query.eq('status', status)
  }

  const { data, error } = await query
  return { data, error }
}

export const approveClaimRequest = async (requestId, providerId, requesterEmail) => {
  // Update the claim request status
  const { error: claimError } = await supabase
    .from('provider_claim_requests')
    .update({
      status: 'approved',
      reviewed_at: new Date().toISOString()
    })
    .eq('id', requestId)

  if (claimError) return { error: claimError }

  // Update the provider's claim status
  if (providerId) {
    const { error: providerError } = await supabase
      .from('service_providers')
      .update({
        claim_status: 'approved',
        updated_at: new Date().toISOString()
      })
      .eq('id', providerId)

    if (providerError) return { error: providerError }
  }

  return { error: null }
}

export const rejectClaimRequest = async (requestId) => {
  const { error } = await supabase
    .from('provider_claim_requests')
    .update({
      status: 'rejected',
      reviewed_at: new Date().toISOString()
    })
    .eq('id', requestId)

  return { error }
}

// ============================================
// GARAGE SCORE COUNT HELPERS
// ============================================

/**
 * Get consistent counts for Garage Score calculation.
 * Used by both Garage and VehicleDetail to ensure scores match.
 * @param {string} vehicleId - Single vehicle ID
 * @returns {object} { services, docs, vals, photos }
 */
export const getVehicleScoreCounts = async (vehicleId) => {
  try {
    const [servicesRes, docsRes, valsRes] = await Promise.all([
      supabase.from('service_records').select('id', { count: 'exact', head: true }).eq('vehicle_id', vehicleId),
      supabase.from('documents').select('id', { count: 'exact', head: true }).eq('vehicle_id', vehicleId),
      supabase.from('vehicle_valuations').select('id', { count: 'exact', head: true }).eq('vehicle_id', vehicleId)
    ])
    return {
      services: servicesRes.count || 0,
      docs: docsRes.count || 0,
      vals: valsRes.count || 0
    }
  } catch (err) {
    console.error('Error getting score counts:', err)
    return { services: 0, docs: 0, vals: 0 }
  }
}

/**
 * Get consistent counts for multiple vehicles at once (for Garage page).
 * @param {string[]} vehicleIds - Array of vehicle IDs
 * @returns {object} Map of vehicleId -> { services, docs, vals }
 */
export const getBatchVehicleScoreCounts = async (vehicleIds) => {
  if (!vehicleIds.length) return {}
  try {
    const [servicesRes, docsRes, valsRes] = await Promise.all([
      supabase.from('service_records').select('vehicle_id').in('vehicle_id', vehicleIds),
      supabase.from('documents').select('vehicle_id').in('vehicle_id', vehicleIds),
      supabase.from('vehicle_valuations').select('vehicle_id').in('vehicle_id', vehicleIds)
    ])

    const counts = {}
    vehicleIds.forEach(id => { counts[id] = { services: 0, docs: 0, vals: 0 } })
    ;(servicesRes.data || []).forEach(r => { if (counts[r.vehicle_id]) counts[r.vehicle_id].services++ })
    ;(docsRes.data || []).forEach(r => { if (counts[r.vehicle_id]) counts[r.vehicle_id].docs++ })
    ;(valsRes.data || []).forEach(r => { if (counts[r.vehicle_id]) counts[r.vehicle_id].vals++ })
    return counts
  } catch (err) {
    console.error('Error getting batch score counts:', err)
    return {}
  }
}

// ============================================
// PUBLIC VEHICLE SHOWCASE HELPERS
// ============================================

export const getPublicVehicle = async (vehicleId) => {
  const { data, error } = await supabase
    .from('vehicles')
    .select(`
      id, year, make, model, trim, color, status, mileage, primary_image_url,
      fuel_type, engine_info, drive_type, created_at,
      owner:profiles!owner_id(id, full_name, member_since, created_at)
    `)
    .eq('id', vehicleId)
    .eq('is_public', true)
    .eq('is_active', true)
    .maybeSingle()
  return { data, error }
}

export const getPublicVehicleStats = async (vehicleId) => {
  const [services, docs, valuations] = await Promise.all([
    supabase.from('service_records').select('id', { count: 'exact', head: true }).eq('vehicle_id', vehicleId),
    supabase.from('documents').select('id', { count: 'exact', head: true }).eq('vehicle_id', vehicleId),
    supabase.from('vehicle_valuations').select('id, value', { count: 'exact' }).eq('vehicle_id', vehicleId)
  ])
  return {
    serviceCount: services.count || 0,
    documentCount: docs.count || 0,
    valuationCount: valuations.count || 0,
    valuationRange: valuations.data?.length > 0 ? {
      min: Math.min(...valuations.data.map(v => v.value)),
      max: Math.max(...valuations.data.map(v => v.value))
    } : null
  }
}

// ============================================
// DISCUSSIONS & COMMENTS
// ============================================

export const getDiscussions = async (category = 'all', sortBy = 'hot') => {
  let query = supabase
    .from('discussions')
    .select(`
      *,
      author:profiles!user_id(id, full_name, avatar_url)
    `)
    .eq('is_active', true)

  if (category && category !== 'all') {
    query = query.eq('category', category)
  }

  switch (sortBy) {
    case 'new':
      query = query.order('created_at', { ascending: false })
      break
    case 'top':
      query = query.order('upvote_count', { ascending: false })
      break
    case 'hot':
    default:
      query = query.order('is_pinned', { ascending: false }).order('created_at', { ascending: false })
      break
  }

  const { data, error } = await query.limit(50)
  return { data, error }
}

export const getDiscussion = async (id) => {
  const { data, error } = await supabase
    .from('discussions')
    .select(`
      *,
      author:profiles!user_id(id, full_name, avatar_url)
    `)
    .eq('id', id)
    .maybeSingle()
  return { data, error }
}

export const createDiscussion = async (discussion) => {
  const { data, error } = await supabase
    .from('discussions')
    .insert(discussion)
    .select(`
      *,
      author:profiles!user_id(id, full_name, avatar_url)
    `)
    .single()
  return { data, error }
}

export const getDiscussionComments = async (discussionId) => {
  const { data, error } = await supabase
    .from('discussion_comments')
    .select(`
      *,
      author:profiles!user_id(id, full_name, avatar_url)
    `)
    .eq('discussion_id', discussionId)
    .is('parent_comment_id', null)
    .order('created_at', { ascending: true })

  if (error) return { data: null, error }

  // Fetch replies for each comment
  const commentIds = data.map(c => c.id)
  if (commentIds.length > 0) {
    const { data: replies } = await supabase
      .from('discussion_comments')
      .select(`
        *,
        author:profiles!user_id(id, full_name, avatar_url)
      `)
      .in('parent_comment_id', commentIds)
      .order('created_at', { ascending: true })

    // Attach replies to their parent comments
    const repliesByParent = {}
    ;(replies || []).forEach(r => {
      if (!repliesByParent[r.parent_comment_id]) repliesByParent[r.parent_comment_id] = []
      repliesByParent[r.parent_comment_id].push(r)
    })
    data.forEach(c => { c.replies = repliesByParent[c.id] || [] })
  }

  return { data, error: null }
}

export const createComment = async (comment) => {
  const { data, error } = await supabase
    .from('discussion_comments')
    .insert(comment)
    .select(`
      *,
      author:profiles!user_id(id, full_name, avatar_url)
    `)
    .single()
  return { data, error }
}

export const toggleDiscussionVote = async (userId, discussionId) => {
  // Check if vote exists
  const { data: existing } = await supabase
    .from('discussion_votes')
    .select('id')
    .eq('user_id', userId)
    .eq('discussion_id', discussionId)
    .maybeSingle()

  if (existing) {
    // Remove vote
    const { error } = await supabase
      .from('discussion_votes')
      .delete()
      .eq('id', existing.id)
    return { voted: false, error }
  } else {
    // Add vote
    const { error } = await supabase
      .from('discussion_votes')
      .insert({ user_id: userId, discussion_id: discussionId })
    return { voted: true, error }
  }
}

export const toggleCommentVote = async (userId, commentId) => {
  const { data: existing } = await supabase
    .from('discussion_votes')
    .select('id')
    .eq('user_id', userId)
    .eq('comment_id', commentId)
    .maybeSingle()

  if (existing) {
    const { error } = await supabase
      .from('discussion_votes')
      .delete()
      .eq('id', existing.id)
    return { voted: false, error }
  } else {
    const { error } = await supabase
      .from('discussion_votes')
      .insert({ user_id: userId, comment_id: commentId })
    return { voted: true, error }
  }
}

export const getUserVotes = async (userId, discussionIds = [], commentIds = []) => {
  const votes = { discussions: new Set(), comments: new Set() }

  if (discussionIds.length > 0) {
    const { data } = await supabase
      .from('discussion_votes')
      .select('discussion_id')
      .eq('user_id', userId)
      .in('discussion_id', discussionIds)
    ;(data || []).forEach(v => votes.discussions.add(v.discussion_id))
  }

  if (commentIds.length > 0) {
    const { data } = await supabase
      .from('discussion_votes')
      .select('comment_id')
      .eq('user_id', userId)
      .in('comment_id', commentIds)
    ;(data || []).forEach(v => votes.comments.add(v.comment_id))
  }

  return votes
}
